import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Text } from '@asigna/kit';
import cx from 'classnames';
import useAddress from 'hooks/use-address';
import { useAtom } from 'jotai/index';
import { isEmpty } from 'lodash';
import { contactsAtom } from 'store';

import { StepOne, StepThree, StepTwo } from './components/steps';
import { useCreateSafe } from './util/useCreateSafe';

export const CreateSafePage: React.FC = () => {
  const history = useHistory();
  const walletAccount = useAddress() || '';
  const [contacts] = useAtom(contactsAtom);
  const {
    selectedStep,
    setSelectedStep,
    safeName,
    setSafeName,
    owners,
    setOwners,
    confirmationThreshold,
    setConfirmationThreshold,
    createSafeError,
    setCreateSafeError,
  } = useCreateSafe();

  useEffect(() => {
    if (isEmpty(walletAccount)) {
      return;
    }
    const meExisted =
      owners.length > 0 && owners[0].name === 'Me' && owners[0].address === walletAccount;
    if (meExisted) {
      return;
    }

    setOwners([{ name: 'Me', address: walletAccount }, ...owners]);
  }, [walletAccount]);

  const handleConfirmationThreshold = (data: number) => {
    setConfirmationThreshold(data);
  };

  return (
    <div className="flex flex-col items-center w-full my-16 overflow-y-scroll px-16 sm:px-0 h-screen gap-24 pb-80">
      <div className="flex items-center gap-4 sm:gap-8">
        <div className="flex flex-col items-center gap-8 min-w-56 sm:min-w-[90px]">
          <div
            className={cx(
              'flex items-center justify-center w-40 h-40 rounded-[50%]',
              selectedStep === 'Step 1'
                ? 'border-primary bg-primary-10 border border-[.5px]'
                : 'bg-black-2'
            )}
          >
            <Text message="1" size="m14" color="regular" />
          </div>
          <Text
            message="Configuration"
            size="m14"
            sizeSm="r13"
            color={selectedStep >= 'Step 1' ? 'white' : 'regular'}
          />
        </div>
        <div
          className={cx(
            'w-32 sm:w-[142px] h-[.5px] mb-20',
            selectedStep > 'Step 1'
              ? 'bg-[linear-gradient(90deg,_#8242f04d_0%,_#7955F5_100%)]'
              : 'bg-[linear-gradient(90deg,_#ffffff0f_0%,__#ffffff33_100%)]'
          )}
        />
        <div className="flex flex-col items-center gap-8 min-w-56 sm:min-w-[90px]">
          <div
            className={cx(
              'flex items-center justify-center w-40 h-40 rounded-[50%]',
              selectedStep === 'Step 1'
                ? 'border border-[.5px] border-white-10'
                : selectedStep === 'Step 2'
                  ? 'border-primary bg-primary-10 border border-[.5px]'
                  : 'bg-black-2'
            )}
          >
            <Text message="2" size="m14" color="regular" />
          </div>
          <Text
            message="Owners"
            size="m14"
            sizeSm="r13"
            color={selectedStep >= 'Step 2' ? 'white' : 'regular'}
          />
        </div>
        <div
          className={cx(
            'w-32 sm:w-[142px] h-[.5px] mb-20',
            selectedStep > 'Step 2'
              ? 'bg-[linear-gradient(90deg,_#8242f04d_0%,_#7955F5_100%)]'
              : 'bg-[linear-gradient(90deg,_#ffffff0f_0%,__#ffffff33_100%)]'
          )}
        />
        <div className="flex flex-col items-center gap-8 min-w-56 sm:min-w-[90px]">
          <div
            className={cx(
              'flex items-center justify-center w-40 h-40 rounded-[50%] border border-[.5px]',
              selectedStep === 'Step 3' ? 'border-primary bg-primary-10' : 'border-white-10'
            )}
          >
            <Text message="3" size="m14" color="regular" />
          </div>
          <Text
            message="Review"
            size="m14"
            sizeSm="r13"
            color={selectedStep === 'Step 3' ? 'white' : 'regular'}
          />
        </div>
      </div>
      {selectedStep === 'Step 1' && (
        <StepOne
          safeName={safeName}
          setSafeName={setSafeName}
          onNextClick={() => {
            setSelectedStep('Step 2');
          }}
          onBackClick={() => history.push('/')}
        />
      )}
      {selectedStep === 'Step 2' && (
        <StepTwo
          owners={owners}
          setOwners={setOwners}
          confirmationThreshold={confirmationThreshold}
          onConfirmationThresholdChange={handleConfirmationThreshold}
          safeContacts={contacts}
          walletAccount={walletAccount}
          onBackClick={() => {
            setSelectedStep('Step 1');
            setCreateSafeError('');
          }}
          onNextClick={() => {
            setSelectedStep('Step 3');
          }}
        />
      )}
      {selectedStep === 'Step 3' && (
        <StepThree
          walletAccount={walletAccount}
          safeName={safeName}
          confirmationThreshold={confirmationThreshold}
          owners={owners}
          errorStatus={createSafeError}
          setCreateSafeError={setCreateSafeError}
          onBackClick={() => {
            setSelectedStep('Step 2');
            setCreateSafeError('');
          }}
        />
      )}
    </div>
  );
};
